import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Trash2, Loader2, Package,
  Upload, DollarSign, ShoppingBag, Eye, Link2, BarChart3,
  Heart, Check, Truck, MapPin, ChevronDown, ChevronUp, Search,
  AlertTriangle, TrendingUp, Info, Download, PackagePlus, Filter
} from 'lucide-react';
import axios from 'axios';
import logger from '../../../utils/logger';
import PopupShopSalesWidget from '../../../components/admin/PopupShopSalesWidget';
import LowStockBanner from '../../../components/admin/LowStockBanner';
import SizeInventoryEditor from '../../../components/admin/SizeInventoryEditor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminShopTab = () => {
  const [items, setItems] = useState([]);
  const [orders, setOrders] = useState([]);
  const [events, setEvents] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState('items');
  const [eventFilter, setEventFilter] = useState('');
  const [stockFilter, setStockFilter] = useState('all'); // all | active | disabled | low_stock | sold_out
  const [sortBy, setSortBy] = useState('recent'); // recent | revenue | sold | stock | name
  const [salesReport, setSalesReport] = useState(null);
  const [restockingId, setRestockingId] = useState(null);
  const emptyForm = { name: '', description: '', price: 0, image: '', image_2: '', inventory: 0, low_stock_threshold: 5, enabled: true, is_featured: false, impact_blurb: '', event_id: '', category: 'merch', variations: [], size_inventory: {} };
  const [form, setForm] = useState(emptyForm);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [updatingOrder, setUpdatingOrder] = useState(null);
  const [orderSearch, setOrderSearch] = useState('');
  const [orderStatusFilter, setOrderStatusFilter] = useState('all');

  const fetchData = useCallback(async () => {
    try {
      const [itemsRes, ordersRes, eventsRes, dashRes, reportRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/shop/items`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/shop/orders`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/shop/events`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/admin/shop/dashboard${eventFilter ? `?event_id=${eventFilter}` : ''}`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/admin/shop/sales-report`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setItems(itemsRes.data);
      setOrders(ordersRes.data);
      setEvents(eventsRes.data);
      setDashboard(dashRes.data);
      setSalesReport(reportRes.data);
    } catch (e) { logger.error(e); }
    setLoading(false);
  }, [eventFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form, price: parseFloat(form.price) || 0, inventory: parseInt(form.inventory) || 0, low_stock_threshold: parseInt(form.low_stock_threshold) || 5, event_id: form.event_id || null, variations: form.variations || [], size_inventory: form.size_inventory || {} };
      if (editingId) {
        await axios.put(`${BACKEND_URL}/api/admin/shop/items/${editingId}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/shop/items`, payload, { withCredentials: true });
      }
      setForm(emptyForm); setShowForm(false); setEditingId(null); fetchData();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  const handleEdit = (item) => {
    setForm({ name: item.name, description: item.description || '', price: item.price, image: item.image || '',
      image_2: item.image_2 || '',
      inventory: item.inventory || 0,
      low_stock_threshold: item.low_stock_threshold ?? 5,
      enabled: item.enabled !== false, is_featured: !!item.is_featured, impact_blurb: item.impact_blurb || '', event_id: item.event_id || '', category: item.category || 'merch',
      variations: item.variations || [],
      size_inventory: item.size_inventory || {} });
    setEditingId(item.id); setShowForm(true);
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.name}"?`)) return;
    try { await axios.delete(`${BACKEND_URL}/api/admin/shop/items/${item.id}`, { withCredentials: true }); fetchData(); }
    catch { alert('Delete failed'); }
  };

  const handleToggle = async (item) => {
    try { await axios.put(`${BACKEND_URL}/api/admin/shop/items/${item.id}`, { enabled: !item.enabled }, { withCredentials: true }); fetchData(); }
    catch { alert('Toggle failed'); }
  };

  const handleRestock = async (item) => {
    const raw = window.prompt(`Restock "${item.name}".\n\nEnter units to ADD (or a negative number to remove):\nCurrent inventory: ${item.inventory || 0}`, '10');
    if (raw === null) return;
    const qty = parseInt(raw);
    if (!qty || isNaN(qty)) { alert('Invalid quantity'); return; }
    setRestockingId(item.id);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/shop/items/${item.id}/restock`, { quantity: qty }, { withCredentials: true });
      fetchData();
      alert(`Inventory updated: ${item.inventory || 0} → ${res.data.inventory} (${qty > 0 ? '+' : ''}${qty})`);
    } catch (e) { alert(e.response?.data?.detail || 'Restock failed'); }
    setRestockingId(null);
  };

  const handleExportSalesCsv = () => {
    if (!salesReport) return;
    const header = ['Item','Price','Sold','Revenue','Inventory','Remaining','Turnover %','Low-Stock Threshold','Status'];
    const rows = salesReport.rows.map(r => [
      r.name, r.price.toFixed(2), r.sold_qty, r.revenue.toFixed(2), r.inventory, r.remaining,
      r.turnover_pct == null ? 'N/A' : r.turnover_pct, r.low_stock_threshold, r.status,
    ]);
    const csv = [header, ...rows].map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `shop-sales-report-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Compute status for each item (for badges + filter)
  const itemStatus = (item) => {
    if (item.enabled === false) return 'disabled';
    const remaining = Math.max((item.inventory || 0) - (item.sold_qty || 0), 0);
    const threshold = item.low_stock_threshold ?? 5;
    if ((item.inventory || 0) > 0 && remaining === 0) return 'sold_out';
    if ((item.inventory || 0) > 0 && 0 < remaining && remaining <= threshold) return 'low_stock';
    return 'active';
  };

  const handleImageUpload = async (e, field = 'image') => {
    const file = e.target.files[0]; if (!file) return;
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await axios.post(`${BACKEND_URL}/api/admin/upload`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(p => ({ ...p, [field]: res.data.url }));
    } catch { alert('Upload failed'); }
    e.target.value = '';
  };

  const getEventName = (eid) => events.find(e => e.id === eid)?.title || eid;

  const handleOrderUpdate = async (orderId, updates) => {
    setUpdatingOrder(orderId);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/shop/orders/${orderId}`, updates, { withCredentials: true });
      fetchData();
    } catch { alert('Update failed'); }
    setUpdatingOrder(null);
  };

  const handleRefund = async (orderId, action) => {
    const msg = action === 'cancel_only'
      ? 'Cancel this order? No refund will be issued.'
      : 'Issue a full refund via Stripe for this order?';
    if (!window.confirm(msg)) return;
    setUpdatingOrder(orderId);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/shop/orders/${orderId}/refund`, { action }, { withCredentials: true });
      alert(res.data.message);
      fetchData();
    } catch (e) {
      alert(e.response?.data?.detail || 'Action failed');
    }
    setUpdatingOrder(null);
  };

  const ss = dashboard?.shop_summary || {};
  const ed = dashboard?.event || null;

  // Status counts for filter chips
  const statusCounts = items.reduce((acc, it) => {
    const s = itemStatus(it);
    acc[s] = (acc[s] || 0) + 1;
    acc.all = (acc.all || 0) + 1;
    return acc;
  }, {});

  let filteredItems = eventFilter ? items.filter(i => i.event_id === eventFilter || !i.event_id) : items;
  if (stockFilter !== 'all') {
    filteredItems = filteredItems.filter(i => itemStatus(i) === stockFilter);
  }
  filteredItems = [...filteredItems].sort((a, b) => {
    if (sortBy === 'revenue') return (b.revenue || 0) - (a.revenue || 0);
    if (sortBy === 'sold') return (b.sold_qty || 0) - (a.sold_qty || 0);
    if (sortBy === 'stock') {
      const ra = Math.max((a.inventory || 0) - (a.sold_qty || 0), 0);
      const rb = Math.max((b.inventory || 0) - (b.sold_qty || 0), 0);
      return ra - rb;  // ascending — lowest stock first
    }
    if (sortBy === 'name') return (a.name || '').localeCompare(b.name || '');
    return 0; // recent: keep server order (created_at desc)
  });
  const filteredOrders = orders.filter(o => {
    const matchEvent = !eventFilter || o.event_id === eventFilter;
    const matchStatus = orderStatusFilter === 'all' ? true :
      orderStatusFilter === 'paid' ? o.fulfillment_status === 'paid' || (o.status === 'completed' && o.fulfillment_status === 'unfulfilled') :
      orderStatusFilter === 'processing' ? o.fulfillment_status === 'processing' :
      orderStatusFilter === 'shipped' ? o.fulfillment_status === 'shipped' :
      orderStatusFilter === 'delivered' ? o.fulfillment_status === 'delivered' :
      orderStatusFilter === 'cancelled' ? o.fulfillment_status === 'cancelled' :
      orderStatusFilter === 'refunded' ? o.fulfillment_status === 'refunded' :
      orderStatusFilter === 'pending' ? o.status === 'pending' :
      true;
    const s = orderSearch.toLowerCase();
    const matchSearch = !s ||
      (o.buyer_name || '').toLowerCase().includes(s) ||
      (o.buyer_email || '').toLowerCase().includes(s) ||
      (o.shipping?.name || '').toLowerCase().includes(s) ||
      (o.shipping?.email || '').toLowerCase().includes(s) ||
      (o.order_number || '').toLowerCase().includes(s) ||
      (o.id || '').toLowerCase().includes(s);
    return matchEvent && matchStatus && matchSearch;
  });

  return (
    <div className="space-y-6" data-testid="shop-admin-tab">
      <LowStockBanner />
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-lg font-bold text-[#0A0A0A]">Shop & Merch</h2>
          <p className="text-xs text-[#4B5563]">Manage items, track sales, and sync with events</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={eventFilter} onChange={(e) => setEventFilter(e.target.value)}
            className="text-xs border border-[#E5E7EB] rounded px-2 py-1.5 focus:border-[#BA1826] focus:outline-none"
            data-testid="shop-event-filter">
            <option value="">All Events</option>
            {events.map(e => <option key={e.id} value={e.id}>{e.title}</option>)}
          </select>
          <a href="/shop" target="_blank" rel="noopener noreferrer"
            className="text-xs font-semibold px-3 py-1.5 rounded border border-[#E5E7EB] text-[#4B5563] hover:bg-[#F8F9FA] flex items-center gap-1">
            <Eye className="w-3 h-3" /> Preview
          </a>
          {!showForm && (
            <button onClick={() => { setForm(emptyForm); setEditingId(null); setShowForm(true); }}
              className="btn-primary text-xs flex items-center gap-1.5" data-testid="add-item-btn">
              <Plus className="w-3.5 h-3.5" /> Add Item
            </button>
          )}
        </div>
      </div>

      {/* Dashboard Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="shop-dashboard-stats">
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 text-center">
          <ShoppingBag className="w-5 h-5 text-[#BA1826] mx-auto mb-1" />
          <div className="font-heading text-xl font-bold text-[#0A0A0A]">{ss.total_sold || 0}</div>
          <p className="text-[10px] text-[#4B5563]">Items Sold</p>
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 text-center">
          <DollarSign className="w-5 h-5 text-[#BA1826] mx-auto mb-1" />
          <div className="font-heading text-xl font-bold text-[#0A0A0A]">${(ss.total_revenue || 0).toLocaleString()}</div>
          <p className="text-[10px] text-[#4B5563]">Shop Revenue</p>
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 text-center">
          <Check className="w-5 h-5 text-green-600 mx-auto mb-1" />
          <div className="font-heading text-xl font-bold text-[#0A0A0A]">{ss.completed_orders || 0}</div>
          <p className="text-[10px] text-[#4B5563]">Completed Orders</p>
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 text-center">
          <Package className="w-5 h-5 text-[#BA1826] mx-auto mb-1" />
          <div className="font-heading text-xl font-bold text-[#0A0A0A]">{ss.item_count || 0}</div>
          <p className="text-[10px] text-[#4B5563]">Active Items</p>
        </div>
      </div>

      <PopupShopSalesWidget />

      {/* Event Dashboard (when event selected) */}
      {ed && (
        <div className="bg-[#0A0A0A] rounded-xl p-5" data-testid="event-dashboard">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-4 h-4 text-[#BA1826]" />
            <h3 className="font-heading text-sm font-bold text-white">{ed.title} — Event Dashboard</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white/10 rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-white">${(ed.total_donations || 0).toLocaleString()}</div>
              <p className="text-[10px] text-gray-400">Donations ({ed.donation_count})</p>
            </div>
            <div className="bg-white/10 rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-white">${(ed.shop_revenue || 0).toLocaleString()}</div>
              <p className="text-[10px] text-gray-400">Shop Sales ({ed.shop_order_count})</p>
            </div>
            <div className="bg-white/10 rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-white">{ed.total_items_committed || 0}</div>
              <p className="text-[10px] text-gray-400">Items Committed</p>
            </div>
            <div className="bg-white/10 rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-white">{ed.total_items_received || 0}</div>
              <p className="text-[10px] text-gray-400">Items Received</p>
            </div>
          </div>
          {ed.fundraising?.enabled && (
            <div className="mt-3">
              <div className="flex items-center justify-between text-xs text-gray-400 mb-1">
                <span>Fundraising Goal</span>
                <span>${(ed.fundraising.current_amount || 0).toLocaleString()} / ${(ed.fundraising.goal_amount || 0).toLocaleString()}</span>
              </div>
              <div className="w-full bg-white/10 rounded-full h-2">
                <div className="bg-[#BA1826] h-2 rounded-full" style={{
                  width: `${ed.fundraising.goal_amount > 0 ? Math.min((ed.fundraising.current_amount / ed.fundraising.goal_amount) * 100, 100) : 0}%`
                }} />
              </div>
            </div>
          )}
          {ed.linked_shop_items?.length > 0 && (
            <div className="mt-3 pt-3 border-t border-white/10">
              <p className="text-[10px] font-semibold text-gray-400 mb-2">Linked Shop Items</p>
              <div className="flex flex-wrap gap-2">
                {ed.linked_shop_items.map(i => (
                  <span key={i.id} className="text-[10px] bg-white/10 text-white px-2 py-1 rounded flex items-center gap-1">
                    <Link2 className="w-2.5 h-2.5" /> {i.name} — {i.sold_qty}/{i.inventory} sold
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Walk-through strip (Phase 3) */}
      <div className="bg-[#FEF9E7] border border-[#F4D03F] rounded-lg p-3 flex items-start gap-2" data-testid="shop-walkthrough">
        <Info className="w-4 h-4 text-[#BA1826] flex-shrink-0 mt-0.5" />
        <div className="text-xs text-[#4B5563]">
          <strong className="text-[#0A0A0A]">Shop admin walk-through:</strong> Use <strong>Items</strong> to add/edit products, quick-restock, and filter by status (Active / Low Stock / Sold Out / Disabled). Use <strong>Orders</strong> to fulfill, ship, and issue Stripe refunds. Use <strong>Sales Report</strong> for product-level revenue, best sellers, low-stock alerts, and CSV export for year-end reporting.
        </div>
      </div>


      {/* Tabs */}
      <div className="flex gap-2 border-b border-[#E5E7EB] pb-2 flex-wrap">
        {['items', 'orders', 'report'].map(t => (
          <button key={t} onClick={() => setView(t)}
            className={`text-xs font-semibold px-3 py-1.5 rounded-t capitalize ${view === t ? 'bg-[#0A0A0A] text-white' : 'text-[#4B5563] hover:bg-[#F8F9FA]'}`}
            data-testid={`shop-tab-${t}`}>
            {t === 'items' ? `Items (${filteredItems.length})` :
             t === 'orders' ? `Orders (${filteredOrders.filter(o => o.status === 'completed').length})` :
             `Sales Report${salesReport?.rows?.length ? ` (${salesReport.rows.length})` : ''}`}
          </button>
        ))}
      </div>

      {/* Item Form */}
      {showForm && (
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-5" data-testid="shop-item-form">
          <h3 className="font-heading font-bold text-sm mb-4">{editingId ? 'Edit Item' : 'Add New Item'}</h3>
          <p className="text-[10px] text-[#4B5563] mb-3">Tip: Fill in inventory and set a low-stock alert threshold to get early warnings when items run low.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <div>
              <label className="text-xs font-medium text-[#4B5563] mb-1 block">Name *</label>
              <input type="text" value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
                placeholder="NHF T-Shirt" className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-md focus:border-[#BA1826] focus:outline-none"
                data-testid="item-form-name" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#4B5563] mb-1 block">Price ($) *</label>
              <input type="number" min="0" step="0.01" value={form.price} onChange={(e) => setForm(p => ({ ...p, price: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-md focus:border-[#BA1826] focus:outline-none"
                data-testid="item-form-price" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#4B5563] mb-1 block">Inventory</label>
              <input type="number" min="0" value={form.inventory} onChange={(e) => setForm(p => ({ ...p, inventory: e.target.value }))}
                placeholder="0 = unlimited" className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-md focus:border-[#BA1826] focus:outline-none"
                data-testid="item-form-inventory" />
              <p className="text-[9px] text-[#94A3B8] mt-0.5">Set to 0 for unlimited stock. When stock runs low, you'll see a warning badge.</p>
            </div>
            <div>
              <label className="text-xs font-medium text-[#4B5563] mb-1 block flex items-center gap-1">
                Low-Stock Alert At <Info className="w-2.5 h-2.5 text-[#94A3B8]" title="Shows a 'Low Stock' badge when remaining ≤ this number. Default: 5." />
              </label>
              <input type="number" min="0" value={form.low_stock_threshold} onChange={(e) => setForm(p => ({ ...p, low_stock_threshold: e.target.value }))}
                placeholder="5" className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-md focus:border-[#BA1826] focus:outline-none"
                data-testid="item-form-low-stock-threshold" />
              <p className="text-[9px] text-[#94A3B8] mt-0.5">Trigger a yellow "Low Stock" badge when remaining units ≤ this number.</p>
            </div>
          </div>
          <div className="mb-3">
            <label className="text-xs font-medium text-[#4B5563] mb-1 block">Description</label>
            <textarea value={form.description} onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))}
              rows={2} placeholder="Short description" className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-md focus:border-[#BA1826] focus:outline-none" />
          </div>
          <div className="mb-3">
            <label className="text-xs font-medium text-[#4B5563] mb-1 block">
              Impact blurb <span className="text-[#9CA3AF] font-normal">(optional · shown under the price on shop + featured cards)</span>
            </label>
            <textarea value={form.impact_blurb}
              onChange={(e) => setForm(p => ({ ...p, impact_blurb: e.target.value }))}
              rows={2} maxLength={180}
              placeholder='e.g. "This shirt helps fund hygiene kits for families in need."'
              className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-md focus:border-[#BA1826] focus:outline-none"
              data-testid="item-form-impact-blurb" />
            <p className="text-[10px] text-[#9CA3AF] mt-0.5">
              Turns a price tag into a mission statement. Leave blank to hide.
              <span className="ml-1 text-[#4B5563]">{(form.impact_blurb || '').length}/180</span>
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="text-xs font-medium text-[#4B5563] mb-1 block">Image 1</label>
              <div className="flex items-center gap-2">
                {form.image && <img src={form.image} alt="" className="w-10 h-10 rounded object-cover border" />}
                <label className="text-xs text-[#BA1826] font-semibold hover:underline cursor-pointer flex items-center gap-1">
                  <Upload className="w-3 h-3" /> {form.image ? 'Change' : 'Upload'}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, 'image')} />
                </label>
                {form.image && <button onClick={() => setForm(p => ({...p, image: ''}))} className="text-[10px] text-red-500 hover:underline">Remove</button>}
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-[#4B5563] mb-1 block">Image 2 (optional)</label>
              <div className="flex items-center gap-2">
                {form.image_2 && <img src={form.image_2} alt="" className="w-10 h-10 rounded object-cover border" />}
                <label className="text-xs text-[#BA1826] font-semibold hover:underline cursor-pointer flex items-center gap-1"
                  data-testid="item-form-image2-upload">
                  <Upload className="w-3 h-3" /> {form.image_2 ? 'Change' : 'Upload'}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, 'image_2')} />
                </label>
                {form.image_2 && <button onClick={() => setForm(p => ({...p, image_2: ''}))} className="text-[10px] text-red-500 hover:underline">Remove</button>}
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-[#4B5563] mb-1 block">Link to Event</label>
              <select value={form.event_id} onChange={(e) => setForm(p => ({ ...p, event_id: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-md focus:border-[#BA1826] focus:outline-none"
                data-testid="item-form-event">
                <option value="">Global (no event)</option>
                {events.map(e => <option key={e.id} value={e.id}>{e.title}</option>)}
              </select>
            </div>
          </div>
          {/* Variations */}
          <div className="mb-3">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-[#4B5563]">Product Options (Size, Color, Style, etc.)</label>
              <button type="button" onClick={() => setForm(p => ({ ...p, variations: [...p.variations, { name: '', options: [''] }] }))}
                className="text-[10px] text-[#BA1826] font-semibold hover:underline flex items-center gap-0.5">
                <Plus className="w-3 h-3" /> Add Option Group
              </button>
            </div>
            {(form.variations || []).map((v, vi) => (
              <div key={vi} className="bg-[#F8F9FA] rounded-lg p-3 mb-2 border border-[#E5E7EB]">
                <div className="flex items-center gap-2 mb-2">
                  <input type="text" value={v.name} placeholder="Option name (e.g. Size, Color)"
                    onChange={(e) => {
                      const vars = [...form.variations];
                      vars[vi] = { ...vars[vi], name: e.target.value };
                      setForm(p => ({ ...p, variations: vars }));
                    }}
                    className="flex-1 px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none" />
                  <button type="button" onClick={() => setForm(p => ({ ...p, variations: p.variations.filter((_, i) => i !== vi) }))}
                    className="text-red-400 hover:text-red-600"><Trash2 className="w-3 h-3" /></button>
                </div>
                <div className="flex flex-wrap gap-1.5 items-center">
                  {(v.options || []).map((opt, oi) => (
                    <div key={oi} className="flex items-center gap-1 bg-white border border-[#E5E7EB] rounded px-2 py-1">
                      <input type="text" value={opt} placeholder="Value"
                        onChange={(e) => {
                          const vars = [...form.variations];
                          const opts = [...vars[vi].options];
                          opts[oi] = e.target.value;
                          vars[vi] = { ...vars[vi], options: opts };
                          setForm(p => ({ ...p, variations: vars }));
                        }}
                        className="w-16 text-[10px] border-none focus:outline-none bg-transparent" />
                      <button type="button" onClick={() => {
                        const vars = [...form.variations];
                        vars[vi] = { ...vars[vi], options: vars[vi].options.filter((_, i) => i !== oi) };
                        setForm(p => ({ ...p, variations: vars }));
                      }} className="text-red-300 hover:text-red-500 text-[10px]">x</button>
                    </div>
                  ))}
                  <button type="button" onClick={() => {
                    const vars = [...form.variations];
                    vars[vi] = { ...vars[vi], options: [...(vars[vi].options || []), ''] };
                    setForm(p => ({ ...p, variations: vars }));
                  }} className="text-[10px] text-[#BA1826] font-semibold px-2 py-1 border border-dashed border-[#BA1826]/30 rounded hover:bg-[#FEF2F2]">
                    + value
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Per-Size Inventory (apparel) */}
          <SizeInventoryEditor
            value={form.size_inventory || {}}
            onChange={(next) => setForm(p => ({ ...p, size_inventory: next }))}
          />

          <div className="flex items-center gap-3 flex-wrap">
            <label className="inline-flex items-center gap-2 text-xs text-[#0A0A0A] cursor-pointer bg-[#FFF8F8] border border-[#BA1826]/30 rounded px-2.5 py-1.5">
              <input type="checkbox" checked={!!form.is_featured}
                onChange={(e) => setForm(p => ({ ...p, is_featured: e.target.checked }))}
                data-testid="item-form-featured-toggle" />
              <span>
                <strong>Featured on homepage</strong>
                <span className="block text-[10px] text-[#4B5563]">Shown in the &ldquo;Support Our Mission Through Merchandise&rdquo; section (up to 4 most recent featured).</span>
              </span>
            </label>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={handleSave} disabled={saving || !form.name.trim() || form.price <= 0}
              className="btn-primary text-xs flex items-center gap-1.5 disabled:opacity-50" data-testid="item-form-save">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              {editingId ? 'Save Changes' : 'Add Item'}
            </button>
            <button onClick={() => { setShowForm(false); setEditingId(null); setForm(emptyForm); }} className="text-xs text-[#4B5563]">Cancel</button>
          </div>
        </div>
      )}

      {/* Items View */}
      {view === 'items' && (
        loading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#BA1826]" /></div> :
        <>
          {/* Filter chips + sort */}
          <div className="bg-white border border-[#E5E7EB] rounded-lg p-3 flex flex-wrap items-center gap-2" data-testid="items-filter-bar">
            <div className="flex items-center gap-1 text-[10px] text-[#4B5563]">
              <Filter className="w-3 h-3" /> Filter:
            </div>
            {[
              { key: 'all', label: 'All', color: 'bg-[#0A0A0A] text-white' },
              { key: 'active', label: 'Active', color: 'bg-green-600 text-white' },
              { key: 'low_stock', label: 'Low Stock', color: 'bg-amber-500 text-white' },
              { key: 'sold_out', label: 'Sold Out', color: 'bg-red-600 text-white' },
              { key: 'disabled', label: 'Disabled', color: 'bg-gray-400 text-white' },
            ].map(chip => (
              <button key={chip.key} onClick={() => setStockFilter(chip.key)}
                className={`text-[10px] font-semibold px-2.5 py-1 rounded-full transition-all ${stockFilter === chip.key ? chip.color : 'bg-[#F1F5F9] text-[#4B5563] hover:bg-[#E5E7EB]'}`}
                data-testid={`stock-filter-${chip.key}`}>
                {chip.label} {typeof statusCounts[chip.key] === 'number' && <span className="opacity-70">({statusCounts[chip.key]})</span>}
              </button>
            ))}
            <div className="ml-auto flex items-center gap-1 text-[10px] text-[#4B5563]">
              Sort:
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
                className="text-[10px] border border-[#E5E7EB] rounded px-2 py-1 focus:border-[#BA1826] focus:outline-none"
                data-testid="items-sort">
                <option value="recent">Most recent</option>
                <option value="revenue">Highest revenue</option>
                <option value="sold">Most sold</option>
                <option value="stock">Lowest stock</option>
                <option value="name">Name (A-Z)</option>
              </select>
            </div>
          </div>

          {filteredItems.length === 0 ? (
            <div className="bg-white border border-[#E5E7EB] rounded-lg p-8 text-center">
              <Package className="w-8 h-8 text-[#9CA3AF] mx-auto mb-2" />
              <p className="text-sm text-[#4B5563]">No shop items{eventFilter ? ' for this event' : ''}{stockFilter !== 'all' ? ` with status "${stockFilter.replace('_',' ')}"` : ''}</p>
            </div>
          ) : (
          <div className="space-y-3">
            {filteredItems.map(item => {
              const remaining = Math.max((item.inventory || 0) - (item.sold_qty || 0), 0);
              const status = itemStatus(item);
              const threshold = item.low_stock_threshold ?? 5;
              const progress = item.inventory > 0 ? Math.min((item.sold_qty / item.inventory) * 100, 100) : 0;

              return (
                <div key={item.id} className={`bg-white border border-[#E5E7EB] rounded-lg overflow-hidden ${!item.enabled ? 'opacity-60' : ''} ${status === 'low_stock' ? 'border-amber-300' : status === 'sold_out' ? 'border-red-300' : ''}`}
                  data-testid={`shop-admin-item-${item.id}`}>
                  <div className="flex flex-col sm:flex-row">
                    <div className="sm:w-28 h-20 sm:h-auto bg-[#F8F9FA] flex-shrink-0 flex">
                      {item.image ? <img src={item.image} alt="" className={`${item.image_2 ? 'w-1/2' : 'w-full'} h-full object-cover`} /> :
                        <div className={`${item.image_2 ? 'w-1/2' : 'w-full'} h-full flex items-center justify-center min-h-[80px]`}><Package className="w-6 h-6 text-[#D1D5DB]" /></div>}
                      {item.image_2 && <img src={item.image_2} alt="" className="w-1/2 h-full object-cover border-l border-[#E5E7EB]" />}
                    </div>
                    <div className="flex-1 p-4">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h3 className="font-heading font-bold text-sm text-[#0A0A0A]">{item.name}</h3>
                        <span className="font-heading text-sm font-bold text-[#BA1826]">${item.price.toFixed(2)}</span>
                        {status === 'disabled' && <span className="text-[10px] font-semibold bg-gray-100 text-gray-600 px-2 py-0.5 rounded uppercase" data-testid={`status-badge-disabled-${item.id}`}>Disabled</span>}
                        {status === 'active' && <span className="text-[10px] font-semibold bg-green-50 text-green-700 px-2 py-0.5 rounded uppercase" data-testid={`status-badge-active-${item.id}`}>Active</span>}
                        {status === 'low_stock' && <span className="text-[10px] font-semibold bg-amber-50 text-amber-700 px-2 py-0.5 rounded uppercase flex items-center gap-0.5" data-testid={`status-badge-low-stock-${item.id}`}><AlertTriangle className="w-2.5 h-2.5" /> Low Stock</span>}
                        {status === 'sold_out' && <span className="text-[10px] font-semibold bg-red-100 text-red-700 px-2 py-0.5 rounded uppercase" data-testid={`status-badge-sold-out-${item.id}`}>Sold Out</span>}
                        {item.event_id && (
                          <span className="text-[10px] font-semibold bg-blue-50 text-blue-700 px-2 py-0.5 rounded flex items-center gap-0.5">
                            <Link2 className="w-2.5 h-2.5" /> {getEventName(item.event_id)}
                          </span>
                        )}
                        {item.is_featured && (
                          <span className="text-[10px] font-semibold bg-[#FFF8F8] text-[#BA1826] border border-[#BA1826]/30 px-2 py-0.5 rounded uppercase tracking-wider" data-testid={`featured-badge-${item.id}`}>
                            ★ Featured
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-xs text-[#4B5563] mb-2">
                        <span>{item.sold_qty || 0} sold{item.inventory > 0 ? ` / ${item.inventory}` : ''}</span>
                        <span>${(item.revenue || 0).toLocaleString()} revenue</span>
                        {item.inventory > 0 && <span className={status === 'low_stock' ? 'text-amber-600 font-semibold' : ''}>{remaining} remaining{status === 'low_stock' ? ` (≤ ${threshold} threshold)` : ''}</span>}
                      </div>
                      {item.inventory > 0 && (
                        <div className="w-full bg-[#F1F5F9] rounded-full h-1.5 mb-2 max-w-xs">
                          <div className={`h-1.5 rounded-full ${status === 'sold_out' ? 'bg-red-600' : status === 'low_stock' ? 'bg-amber-500' : 'bg-[#BA1826]'}`} style={{ width: `${progress}%` }} />
                        </div>
                      )}
                      <div className="flex items-center gap-2 flex-wrap">
                        <button onClick={() => handleEdit(item)} className="text-[10px] font-semibold px-2.5 py-1 rounded border border-[#E5E7EB] text-[#4B5563] hover:bg-[#F8F9FA]" data-testid={`edit-item-${item.id}`}>Edit</button>
                        <button onClick={() => handleRestock(item)} disabled={restockingId === item.id}
                          className="text-[10px] font-semibold px-2.5 py-1 rounded border border-green-300 text-green-700 hover:bg-green-50 flex items-center gap-1 disabled:opacity-50"
                          data-testid={`restock-item-${item.id}`}>
                          {restockingId === item.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <PackagePlus className="w-3 h-3" />} Restock
                        </button>
                        <button onClick={() => handleToggle(item)} className="text-[10px] font-semibold px-2.5 py-1 rounded border border-[#E5E7EB] text-[#4B5563] hover:bg-[#F8F9FA]" data-testid={`toggle-item-${item.id}`}>
                          {item.enabled ? 'Disable' : 'Enable'}
                        </button>
                        <button onClick={async () => {
                          try {
                            await axios.put(`${BACKEND_URL}/api/admin/shop/items/${item.id}`,
                              { is_featured: !item.is_featured }, { withCredentials: true });
                            fetchData();
                          } catch { alert('Featured toggle failed'); }
                        }}
                          className={`text-[10px] font-semibold px-2.5 py-1 rounded border ${item.is_featured ? 'border-[#BA1826] text-[#BA1826] bg-[#FFF8F8]' : 'border-[#E5E7EB] text-[#4B5563] hover:bg-[#F8F9FA]'}`}
                          data-testid={`toggle-featured-${item.id}`}>
                          {item.is_featured ? '★ Unfeature' : '☆ Feature'}
                        </button>
                        <button onClick={() => handleDelete(item)} className="text-[10px] font-semibold px-2.5 py-1 rounded border border-red-200 text-red-500 hover:bg-red-50 flex items-center gap-1" data-testid={`delete-item-${item.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          )}
        </>
      )}

      {/* Orders View */}
      {view === 'orders' && (
        <>
        {/* Order Search + Status Filter */}
        <div className="flex gap-2 flex-wrap items-center mb-3">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
            <input type="text" value={orderSearch} onChange={(e) => setOrderSearch(e.target.value)}
              placeholder="Search by name, email, order ID..."
              className="w-full pl-9 pr-3 py-2 border border-[#E5E7EB] rounded text-xs focus:border-[#BA1826] focus:outline-none"
              data-testid="order-search" />
          </div>
          <select value={orderStatusFilter} onChange={(e) => setOrderStatusFilter(e.target.value)}
            className="px-3 py-2 border border-[#E5E7EB] rounded text-xs focus:border-[#BA1826] focus:outline-none"
            data-testid="order-status-filter">
            <option value="all">All Orders ({orders.length})</option>
            <option value="paid">Paid</option>
            <option value="processing">Processing</option>
            <option value="shipped">Shipped</option>
            <option value="delivered">Delivered</option>
            <option value="cancelled">Cancelled</option>
            <option value="refunded">Refunded</option>
            <option value="pending">Pending Payment</option>
          </select>
        </div>
        {filteredOrders.length === 0 ? (
          <div className="bg-white border border-[#E5E7EB] rounded-lg p-8 text-center">
            <ShoppingBag className="w-8 h-8 text-[#9CA3AF] mx-auto mb-2" />
            <p className="text-sm text-[#4B5563]">No orders{eventFilter ? ' for this event' : ''}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredOrders.map(o => {
              const isExpanded = expandedOrder === o.id;
              const shipping = o.shipping || {};
              const statusColors = {
                unfulfilled: 'bg-gray-100 text-gray-600',
                paid: 'bg-green-100 text-green-700',
                processing: 'bg-blue-100 text-blue-700',
                packed: 'bg-indigo-100 text-indigo-700',
                shipped: 'bg-purple-100 text-purple-700',
                delivered: 'bg-emerald-100 text-emerald-700',
                cancelled: 'bg-red-100 text-red-700',
                refunded: 'bg-orange-100 text-orange-700',
              };
              return (
                <div key={o.id} className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden" data-testid={`order-card-${o.id}`}>
                  <button onClick={() => setExpandedOrder(isExpanded ? null : o.id)}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#F8F9FA] transition-colors text-left">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-mono text-[10px] bg-[#F1F5F9] text-[#4B5563] px-2 py-0.5 rounded">{o.order_number || o.id}</span>
                      <span className="text-xs font-medium">{o.buyer_name || shipping.name || 'Anonymous'}</span>
                      <span className="text-xs text-[#BA1826] font-bold">${o.total?.toFixed(2)}</span>
                      <span className={`text-[9px] font-semibold px-2 py-0.5 rounded ${o.status === 'completed' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>{o.status}</span>
                      <span className={`text-[9px] font-semibold px-2 py-0.5 rounded ${statusColors[o.fulfillment_status] || 'bg-gray-100 text-gray-500'}`}>{o.fulfillment_status || 'unfulfilled'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-[#9CA3AF]">
                      <span>{o.created_at ? new Date(o.created_at).toLocaleDateString() : ''}</span>
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </div>
                  </button>
                  {isExpanded && (
                    <div className="border-t border-[#E5E7EB] px-4 py-4 space-y-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        {/* Items */}
                        <div>
                          <p className="text-[10px] font-bold text-[#4B5563] uppercase mb-2">Items Ordered</p>
                          {(o.line_items || []).map((li, idx) => (
                            <div key={idx} className="flex items-center justify-between text-xs py-1 border-b border-[#F1F5F9] last:border-0">
                              <div>
                                <span className="font-medium">{li.item_name}</span>
                                {li.selected_options && Object.keys(li.selected_options).length > 0 && (
                                  <span className="text-[#9CA3AF] ml-1">({Object.entries(li.selected_options).map(([k,v]) => `${k}: ${v}`).join(', ')})</span>
                                )}
                              </div>
                              <span className="text-[#4B5563]">x{li.quantity} — ${li.line_total?.toFixed(2)}</span>
                            </div>
                          ))}
                          {!o.line_items?.length && <p className="text-xs text-[#9CA3AF]">{o.item_name} x{o.quantity}</p>}
                        </div>
                        {/* Shipping */}
                        <div>
                          <p className="text-[10px] font-bold text-[#4B5563] uppercase mb-2">Shipping Address</p>
                          {shipping.name ? (
                            <div className="text-xs text-[#4B5563] space-y-0.5">
                              <p className="font-medium">{shipping.name}</p>
                              <p>{shipping.address}{shipping.apartment ? `, ${shipping.apartment}` : ''}</p>
                              <p>{shipping.city}, {shipping.state} {shipping.zip}</p>
                              {shipping.email && <p className="text-[#BA1826]">{shipping.email}</p>}
                              {shipping.phone && <p>{shipping.phone}</p>}
                              {shipping.notes && <p className="italic text-[#9CA3AF] mt-1">Notes: {shipping.notes}</p>}
                            </div>
                          ) : (
                            <p className="text-xs text-[#9CA3AF]">No shipping info (legacy order)</p>
                          )}
                        </div>
                      </div>
                      {/* Fulfillment Controls */}
                      <div className="bg-[#F8F9FA] rounded-lg p-3 space-y-3">
                        <p className="text-[10px] font-bold text-[#4B5563] uppercase">Fulfillment</p>
                        <div className="grid sm:grid-cols-2 gap-3">
                          <div>
                            <label className="text-[10px] text-[#4B5563] mb-1 block">Status</label>
                            <select value={o.fulfillment_status || 'unfulfilled'}
                              onChange={(e) => handleOrderUpdate(o.id, { fulfillment_status: e.target.value })}
                              className="w-full px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none"
                              data-testid={`fulfillment-status-${o.id}`}>
                              {['unfulfilled','paid','processing','packed','shipped','delivered','cancelled','refunded'].map(s => (
                                <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="text-[10px] text-[#4B5563] mb-1 block">Carrier</label>
                            <input type="text" value={o.carrier || ''} placeholder="USPS, UPS, FedEx..."
                              onChange={(e) => handleOrderUpdate(o.id, { carrier: e.target.value })}
                              className="w-full px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none" />
                          </div>
                          <div>
                            <label className="text-[10px] text-[#4B5563] mb-1 block">Tracking Number</label>
                            <input type="text" value={o.tracking_number || ''} placeholder="Tracking #"
                              onChange={(e) => handleOrderUpdate(o.id, { tracking_number: e.target.value })}
                              className="w-full px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none"
                              data-testid={`tracking-${o.id}`} />
                          </div>
                          <div>
                            <label className="text-[10px] text-[#4B5563] mb-1 block">Shipped Date</label>
                            <input type="date" value={o.shipped_date || ''}
                              onChange={(e) => handleOrderUpdate(o.id, { shipped_date: e.target.value })}
                              className="w-full px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none" />
                          </div>
                        </div>
                        <div>
                          <label className="text-[10px] text-[#4B5563] mb-1 block">Internal Notes</label>
                          <textarea value={o.internal_notes || ''} rows={2} placeholder="Internal notes..."
                            onBlur={(e) => handleOrderUpdate(o.id, { internal_notes: e.target.value })}
                            onChange={(e) => {
                              const val = e.target.value;
                              setOrders(prev => prev.map(ord => ord.id === o.id ? { ...ord, internal_notes: val } : ord));
                            }}
                            className="w-full px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none resize-none" />
                        </div>
                        {/* Cancel / Refund Actions */}
                        {o.fulfillment_status !== 'refunded' && o.fulfillment_status !== 'cancelled' && o.status === 'completed' && (
                          <div className="flex gap-2 pt-2 border-t border-[#E5E7EB]">
                            <button onClick={() => handleRefund(o.id, 'cancel_only')} disabled={updatingOrder === o.id}
                              className="text-[10px] font-semibold px-3 py-1.5 rounded border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50"
                              data-testid={`cancel-order-${o.id}`}>
                              Cancel Only
                            </button>
                            <button onClick={() => handleRefund(o.id, 'refund')} disabled={updatingOrder === o.id}
                              className="text-[10px] font-semibold px-3 py-1.5 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 flex items-center gap-1"
                              data-testid={`refund-order-${o.id}`}>
                              <DollarSign className="w-3 h-3" /> Full Refund via Stripe
                            </button>
                          </div>
                        )}
                        {o.fulfillment_status === 'refunded' && (
                          <div className="pt-2 border-t border-[#E5E7EB] space-y-1">
                            <p className="text-[10px] font-bold text-red-600 uppercase">Refund Details</p>
                            <div className="grid grid-cols-2 gap-2 text-[10px]">
                              <div><span className="text-[#9CA3AF]">Refund Amount:</span> <span className="font-semibold text-red-600">${o.refund_amount?.toFixed(2) || '—'}</span></div>
                              <div><span className="text-[#9CA3AF]">Refund Date:</span> <span className="font-medium">{o.refunded_at ? new Date(o.refunded_at).toLocaleDateString() : '—'}</span></div>
                              <div><span className="text-[#9CA3AF]">Stripe Refund ID:</span> <span className="font-mono text-[9px]">{o.refund_id || '—'}</span></div>
                              <div><span className="text-[#9CA3AF]">Payment Intent:</span> <span className="font-mono text-[9px]">{o.payment_intent_id || o.stripe_session_id?.substring(0, 20) || '—'}</span></div>
                              <div><span className="text-[#9CA3AF]">Refund Status:</span> <span className="font-medium">{o.refund_status || '—'}</span></div>
                              <div><span className="text-[#9CA3AF]">Original Total:</span> <span className="font-medium">${o.total?.toFixed(2)}</span></div>
                            </div>
                          </div>
                        )}
                        {o.fulfillment_status === 'cancelled' && (
                          <div className="pt-2 border-t border-[#E5E7EB]">
                            <p className="text-[10px] font-semibold text-gray-500">Cancelled — no Stripe refund issued. Original charge of ${o.total?.toFixed(2)} was not reversed.</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        </>
      )}

      {/* Sales Report View */}
      {view === 'report' && (
        loading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#BA1826]" /></div> :
        !salesReport ? (
          <div className="bg-white border border-[#E5E7EB] rounded-lg p-8 text-center">
            <BarChart3 className="w-8 h-8 text-[#9CA3AF] mx-auto mb-2" />
            <p className="text-sm text-[#4B5563]">Sales report is not available yet.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="sales-report-view">
            {/* Walk-through / help text */}
            <div className="bg-[#FEF9E7] border border-[#F4D03F] rounded-lg p-3 flex items-start gap-2">
              <Info className="w-4 h-4 text-[#BA1826] flex-shrink-0 mt-0.5" />
              <div className="text-xs text-[#4B5563]">
                <strong className="text-[#0A0A0A]">How to use this report:</strong> Each row shows how a product is performing — units sold, revenue earned, stock remaining, and turnover rate. Use the <strong>best sellers</strong> list to plan restocks, check <strong>low-stock alerts</strong> daily, and <strong>export CSV</strong> for board reports or year-end reconciliation.
              </div>
            </div>

            {/* KPI row */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="sales-report-kpis">
              <div className="bg-white border border-[#E5E7EB] rounded-lg p-3 text-center">
                <div className="text-[9px] uppercase text-[#4B5563] tracking-wider">Revenue</div>
                <div className="font-heading text-lg font-bold text-[#0A0A0A]">${(salesReport.totals.total_revenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              </div>
              <div className="bg-white border border-[#E5E7EB] rounded-lg p-3 text-center">
                <div className="text-[9px] uppercase text-[#4B5563] tracking-wider">Units Sold</div>
                <div className="font-heading text-lg font-bold text-[#0A0A0A]">{salesReport.totals.total_sold}</div>
              </div>
              <div className="bg-white border border-[#E5E7EB] rounded-lg p-3 text-center">
                <div className="text-[9px] uppercase text-[#4B5563] tracking-wider">Items</div>
                <div className="font-heading text-lg font-bold text-[#0A0A0A]">{salesReport.totals.item_count}</div>
              </div>
              <div className="bg-white border border-[#E5E7EB] rounded-lg p-3 text-center">
                <div className="text-[9px] uppercase text-green-700 tracking-wider">Active</div>
                <div className="font-heading text-lg font-bold text-green-700">{salesReport.totals.active_items}</div>
              </div>
              <div className="bg-white border border-[#E5E7EB] rounded-lg p-3 text-center">
                <div className="text-[9px] uppercase text-amber-700 tracking-wider">Low Stock</div>
                <div className="font-heading text-lg font-bold text-amber-700">{salesReport.totals.low_stock_count}</div>
              </div>
              <div className="bg-white border border-[#E5E7EB] rounded-lg p-3 text-center">
                <div className="text-[9px] uppercase text-red-700 tracking-wider">Sold Out</div>
                <div className="font-heading text-lg font-bold text-red-700">{salesReport.totals.sold_out_count}</div>
              </div>
            </div>

            {/* Best sellers */}
            {salesReport.best_sellers.length > 0 && (
              <div className="bg-white border border-[#E5E7EB] rounded-lg p-4" data-testid="best-sellers-panel">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp className="w-4 h-4 text-[#BA1826]" />
                  <h3 className="font-heading text-sm font-bold text-[#0A0A0A]">Best Sellers</h3>
                  <span className="text-[10px] text-[#4B5563]">Top {salesReport.best_sellers.length} by units sold</span>
                </div>
                <div className="space-y-2">
                  {salesReport.best_sellers.map((r, i) => (
                    <div key={r.id} className="flex items-center gap-3" data-testid={`best-seller-${i + 1}`}>
                      <div className="w-6 h-6 bg-[#BA1826] text-white rounded-full flex items-center justify-center text-[10px] font-bold">{i + 1}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-[#0A0A0A] truncate">{r.name}</p>
                        <p className="text-[10px] text-[#4B5563]">{r.sold_qty} sold · ${r.revenue.toLocaleString()} revenue</p>
                      </div>
                      {r.turnover_pct != null && (
                        <span className="text-[10px] bg-[#F1F5F9] text-[#4B5563] px-2 py-0.5 rounded font-semibold">{r.turnover_pct}% sold</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Low stock alerts */}
            {salesReport.low_stock_items.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="low-stock-alert">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600" />
                  <h3 className="font-heading text-sm font-bold text-amber-800">Low-Stock Alerts ({salesReport.low_stock_items.length})</h3>
                </div>
                <p className="text-[10px] text-amber-700 mb-2">Products at or below their low-stock threshold. Click Restock to add more.</p>
                <div className="space-y-1.5">
                  {salesReport.low_stock_items.map(r => (
                    <div key={r.id} className="flex items-center justify-between text-xs bg-white rounded px-3 py-2">
                      <span className="font-medium text-[#0A0A0A]">{r.name}</span>
                      <span className="text-amber-700 font-semibold">{r.remaining} left (≤ {r.low_stock_threshold})</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Full table */}
            <div className="bg-white border border-[#E5E7EB] rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-[#BA1826]" />
                  <h3 className="font-heading text-sm font-bold text-[#0A0A0A]">Product Performance</h3>
                </div>
                <button onClick={handleExportSalesCsv}
                  className="text-[10px] font-semibold px-2.5 py-1.5 rounded bg-[#0A0A0A] text-white hover:bg-[#1a1a1a] flex items-center gap-1"
                  data-testid="export-sales-csv">
                  <Download className="w-3 h-3" /> Export CSV
                </button>
              </div>
              <p className="text-[10px] text-[#4B5563] mb-3">Tip: "Turnover %" = units sold ÷ total inventory. A higher % means faster-moving products.</p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="sales-report-table">
                  <thead>
                    <tr className="text-left text-[9px] uppercase text-[#4B5563] border-b border-[#E5E7EB]">
                      <th className="py-2 pr-3">Item</th>
                      <th className="py-2 pr-3">Price</th>
                      <th className="py-2 pr-3">Sold</th>
                      <th className="py-2 pr-3">Revenue</th>
                      <th className="py-2 pr-3">Remaining</th>
                      <th className="py-2 pr-3">Turnover</th>
                      <th className="py-2 pr-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {salesReport.rows.map(r => (
                      <tr key={r.id} className="border-b border-[#F1F5F9]">
                        <td className="py-2 pr-3 font-medium text-[#0A0A0A]">{r.name}</td>
                        <td className="py-2 pr-3 text-[#4B5563]">${r.price.toFixed(2)}</td>
                        <td className="py-2 pr-3 text-[#4B5563]">{r.sold_qty}</td>
                        <td className="py-2 pr-3 text-[#4B5563] font-semibold">${r.revenue.toFixed(2)}</td>
                        <td className="py-2 pr-3 text-[#4B5563]">{r.inventory === 0 ? 'unlimited' : r.remaining}</td>
                        <td className="py-2 pr-3 text-[#4B5563]">{r.turnover_pct == null ? '—' : `${r.turnover_pct}%`}</td>
                        <td className="py-2 pr-3">
                          {r.status === 'active' && <span className="text-[9px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-semibold uppercase">Active</span>}
                          {r.status === 'disabled' && <span className="text-[9px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded font-semibold uppercase">Disabled</span>}
                          {r.status === 'low_stock' && <span className="text-[9px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-semibold uppercase">Low Stock</span>}
                          {r.status === 'sold_out' && <span className="text-[9px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-semibold uppercase">Sold Out</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )
      )}

    </div>
  );
};

export default AdminShopTab;
